import { Check, ArrowRight, Sparkles } from "lucide-react";
import { PACKAGES } from "@/constants/testIds";

const packages = [
  {
    name: "Starter Growth Plan",
    tagline: "For small businesses that need SEO, visibility and basic lead generation.",
    features: [
      "On-page & technical SEO",
      "Keyword strategy & content briefs",
      "Google Business Profile optimization",
      "Basic lead generation setup",
      "Monthly reporting",
    ],
    ctaTestId: PACKAGES.starterCta,
    highlight: false,
  },
  {
    name: "Performance Growth Plan",
    tagline: "For businesses that want SEO, Google Ads, Meta Ads, landing page and conversion improvement.",
    features: [
      "Everything in Starter",
      "Google Ads & Meta Ads management",
      "Landing page optimization & A/B testing",
      "Conversion tracking & attribution",
      "Bi-weekly strategy calls",
    ],
    ctaTestId: PACKAGES.performanceCta,
    highlight: true,
  },
  {
    name: "AI Automation Growth Plan",
    tagline: "For businesses ready to scale with AI-powered marketing systems.",
    features: [
      "Everything in Performance",
      "CRM workflows & lead follow-up automation",
      "Email & lifecycle automation",
      "AI-powered reporting dashboards",
      "Content operations with AI",
    ],
    ctaTestId: PACKAGES.aiCta,
    highlight: false,
  },
];

export default function Packages() {
  return (
    <section id="packages" className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="max-w-3xl reveal">
          <div className="text-xs font-bold uppercase tracking-widest text-blue-600">Packages</div>
          <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-slate-900 tracking-tight">
            Flexible plans, built around your growth goals.
          </h2>
          <p className="mt-4 text-slate-600 text-base md:text-lg">
            Every business is different. Request a custom plan and I&apos;ll tailor it to your industry, budget and objectives.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {packages.map((p, i) => (
            <div
              key={p.name}
              data-testid={`package-card-${i}`}
              className={`reveal relative rounded-3xl p-8 border transition-all flex flex-col ${
                p.highlight
                  ? "bg-slate-900 text-white border-slate-900 shadow-2xl md:-translate-y-4"
                  : "bg-white border-slate-200 hover:border-blue-200 hover:shadow-lg text-slate-900"
              }`}
            >
              {p.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-blue-600 text-white text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full shadow-lg">
                  <Sparkles className="w-3 h-3" /> Most Popular
                </div>
              )}
              <h3 className={`font-display font-bold text-2xl ${p.highlight ? "text-white" : "text-slate-900"}`}>
                {p.name}
              </h3>
              <p className={`mt-2 text-sm ${p.highlight ? "text-slate-300" : "text-slate-600"}`}>{p.tagline}</p>

              <ul className="mt-6 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.highlight ? "text-blue-400" : "text-blue-600"}`} />
                    <span className={p.highlight ? "text-slate-200" : "text-slate-700"}>{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href="#free-audit-form"
                data-testid={p.ctaTestId}
                className={`mt-8 inline-flex items-center justify-center gap-2 font-semibold py-3.5 px-6 rounded-full transition-all active:scale-95 ${
                  p.highlight
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-slate-900 text-white hover:bg-slate-800"
                }`}
              >
                Request Custom Plan <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
